import { DeductionDocument } from "@/core/entities/deduction";
import { Button, Chip, Flex, Icon, Icons, Skeleton, Span, TableColumnType, Tooltip } from "@ir/ui";
import { ReactNode } from "react";
import useDeductionIncomplete from "./hook/useDeductionIncomplete";

const MAX_FILE_NAME_LENGTH = 45;

const DocumentSkeleton = (width: number) => <Skeleton height={9} width={width} animation="wave" variant="rounded" className="mt-1 mb-1" />;

const getFileNameDisplay = (fileName: string) => {
  const fileExtension = fileName?.split(".").pop();
  const fileNameWithoutExtension = fileName?.split(".").slice(0, -1).join(".");
  return fileName?.length > MAX_FILE_NAME_LENGTH ? fileNameWithoutExtension?.slice(0, MAX_FILE_NAME_LENGTH) + "..." + fileExtension : fileName;
};

interface DataType {
  operation?: ReactNode;
}

interface BackupDocumentsTableProps {
  isLoadingDeductionStatus: boolean;
  workspaceSettings: any;
  downloadingDocuments: number[];
  deletingDocuments: number[];
  handleDocumentDownload: (deductionBackupDocument: DeductionDocument) => void | Promise<void>;
  handleDocumentDelete: (backupDocumentId: number) => void | Promise<void>;
}

const BackupDocumentsTableColumns = ({
  workspaceSettings,
  downloadingDocuments,
  deletingDocuments,
  handleDocumentDownload,
  handleDocumentDelete,
}: BackupDocumentsTableProps): TableColumnType<DeductionDocument & DataType>[] => {
  const { importedFilesAsDocuments, deductionResolutionStatus, isLoadingDeductionStatus } = useDeductionIncomplete();
  const getFileIcon = (record: DeductionDocument) => {
    if (record.isProcessed === "FAILED") return Icons.AlertCircle;
    if (record.isProcessed === "PENDING" || downloadingDocuments.includes(record.id) || deletingDocuments.includes(record.id)) return Icons.Loading;
    return Icons.File;
  };

  const getFileIconClass = (record: DeductionDocument) => {
    if (downloadingDocuments.includes(record.id) || record.isProcessed === "PENDING" || deletingDocuments.includes(record.id)) return "icon-fast-loading";
    return "";
  };

  const columns: TableColumnType<DeductionDocument & DataType>[] = [
    {
      title: "File Name",
      width: 280,
      dataIndex: "fileDisplayName" as keyof DeductionDocument,
      key: "name",
      align: "left",
      cellRender: (value, record) => {
        if (isLoadingDeductionStatus) {
          return DocumentSkeleton(150);
        }

        const fileDisplayName = value as string;
        const fileNameDisplay =
          fileDisplayName.length > MAX_FILE_NAME_LENGTH ? (
            <Tooltip content={fileDisplayName} placement="top start" className="file-name-tooltip">
              <Button
                noStyle
                className="text-secondary-600 font-medium"
                style={
                  downloadingDocuments.includes(record.id) || record.isProcessed === "PENDING" || deletingDocuments.includes(record.id)
                    ? { cursor: "not-allowed", whiteSpace: "break-spaces", textAlign: "left", wordBreak: "break-all" }
                    : { whiteSpace: "break-spaces", textAlign: "left", wordBreak: "break-all" }
                }
                onPress={() => handleDocumentDownload(record)}
                isDisabled={downloadingDocuments.includes(record.id) || record.isProcessed === "PENDING" || deletingDocuments.includes(record.id)}
              >
                {getFileNameDisplay(fileDisplayName)}
              </Button>
            </Tooltip>
          ) : (
            <Button
              noStyle
              className="text-secondary-600 font-medium"
              style={
                downloadingDocuments.includes(record.id) || record.isProcessed === "PENDING" || deletingDocuments.includes(record.id)
                  ? { cursor: "not-allowed", whiteSpace: "break-spaces", textAlign: "left", wordBreak: "break-all" }
                  : { whiteSpace: "break-spaces", textAlign: "left", wordBreak: "break-all" }
              }
              onPress={() => handleDocumentDownload(record)}
              isDisabled={downloadingDocuments.includes(record.id) || record.isProcessed === "PENDING" || deletingDocuments.includes(record.id)}
            >
              {fileDisplayName}{" "}
            </Button>
          );
        return (
          <Flex align="center" gap={0.75} wrap="no-wrap">
            <Span className="flex">
              <Icon
                size="small"
                htmlTitle={record.isProcessed === "FAILED" ? "Invalid Document" : ""}
                name={getFileIcon(record)}
                color={record.isProcessed === "FAILED" ? "error" : "grey"}
                className={getFileIconClass(record)}
              />
            </Span>
            <Span>{fileNameDisplay}</Span>
          </Flex>
        );
      },
    },
    {
      title: "Date Uploaded",
      width: 120,
      dataIndex: "createdAt" as keyof DeductionDocument,
      key: "date",
      align: "left",
      cellRender: (value, record) => {
        if (isLoadingDeductionStatus) {
          return DocumentSkeleton(150);
        }
        return workspaceSettings?.displayDateFormat ? record.createdAtFormatted(workspaceSettings.displayDateFormat) : value;
      },
    },
    {
      title: "Tags",
      width: 150,
      dataIndex: "documentType" as keyof DeductionDocument,
      key: "tags",
      align: "left",
      cellRender: value =>
        isLoadingDeductionStatus ? (
          DocumentSkeleton(100)
        ) : (
          <Chip
            size="small"
            color="grey"
            label={
              <Flex justifyContent="end">
                <Flex align="center" gap={0.75} wrap="no-wrap">
                  <Span className="flex">{value}</Span>
                </Flex>
              </Flex>
            }
            className="chip-document-type bg-grey-50-transparent border-color-grey-200 text-grey-700"
          />
        ),
    },
    {
      title: "",
      width: 30,
      dataIndex: "operation" as keyof DataType,
      key: "operation",
      align: "right",
      cellRender: (_, record) => (
        <Flex justifyContent="end">
          {isLoadingDeductionStatus
            ? DocumentSkeleton(22)
            : record.deletable &&
              !importedFilesAsDocuments.some(doc => doc.isProcessed === "PENDING") &&
              !deletingDocuments.includes(record.id) &&
              (record.isProcessed === "FAILED" || deductionResolutionStatus !== "processing") && (
                <Tooltip content="Delete document" placement="top start">
                  <Button noStyle size="small" onPress={() => handleDocumentDelete(record.id)} isDisabled={deletingDocuments.length != 0}>
                    <Icon slot="end" color="grey" name={Icons.Close} />
                  </Button>
                </Tooltip>
              )}
        </Flex>
      ),
    },
  ];
  return columns;
};

export default BackupDocumentsTableColumns;
